import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Alert,
  Button,
  Chip,
  IconButton,
  Snackbar,
  Switch,
  Grid,
} from '@mui/material';
import { ArrowBack, AccountTree, Mail, Warehouse, Inventory, OpenInNew, ContentCopy, Apps, Send, Article, Folder, InsertDriveFile, Description, Group, EditOutlined } from '@mui/icons-material';
import { companyService } from '../index';
import CompanyAreas from '../components/CompanyAreas';
import CompanyCorrespondenceTypes from '../components/CompanyCorrespondenceTypes';
import CompanyWarehouses from '../components/CompanyWarehouses';
import CompanyBoxes from '../components/CompanyBoxes';
import CompanyModalForm from '../components/CompanyModalForm';
import LoadingLogo from '../../../components/LoadingLogo';
import PageMasthead from '../../../components/PageMasthead';
import { useAuth } from '../../../hooks/useAuth';

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isOwner } = useAuth();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [modulesConfig, setModulesConfig] = useState({});
  const [modulesSaving, setModulesSaving] = useState(false);
  const [modulesSaved, setModulesSaved] = useState(false);

  const MODULE_LIST = [
    { key: 'correspondences', label: 'Correspondencia', icon: Send },
    { key: 'templates', label: 'Plantillas', icon: Article },
    { key: 'proceedings', label: 'Expedientes', icon: Folder },
    { key: 'documents', label: 'Dashboard Doc.', icon: InsertDriveFile },
    { key: 'retentions', label: 'Retencion', icon: Description },
    { key: 'entities', label: 'Externos', icon: Group },
    { key: 'warehouses', label: 'Almacenes', icon: Warehouse },
  ];

  useEffect(() => {
    loadCompany();
  }, [id]);

  useEffect(() => {
    if (company) {
      setModulesConfig(company.modules || {});
    }
  }, [company]);

  const loadCompany = async () => {
    try {
      setLoading(true);
      const response = await companyService.getById(id);
      setCompany(response.data);
    } catch (error) {
      setError('Error al cargar la empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LoadingLogo size={120} />
      </Box>
    );
  }

  if (error || !company) {
    return (
      <Box>
        <Alert severity="error">{error || 'Empresa no encontrada'}</Alert>
        <Button onClick={() => navigate('/companies')} sx={{ mt: 2 }}>
          Volver a Empresas
        </Button>
      </Box>
    );
  }

  const InfoField = ({ label, children }) => (
    <Box>
      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(29,31,32,0.55)', mb: 0.5 }}>
        {label}
      </Typography>
      <Box>{children}</Box>
    </Box>
  );

  return (
    <Box>
      {/* Masthead */}
      <PageMasthead
        title={company.name}
        subtitle="Configuracion de la empresa: areas, tipos de correspondencia, bodegas y cajas."
        stat={company.isActive ? 'Activa' : 'Inactiva'}
        statLabel="Estado"
      />

      {/* Back + Actions row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 3 }}>
        {isOwner ? (
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/companies')}
            sx={{ color: '#5980a6', fontWeight: 700, fontSize: '0.9375rem' }}
          >
            Empresas
          </Button>
        ) : <Box />}
        {isOwner && (
          <Button
            variant="outlined"
            startIcon={<EditOutlined />}
            size="small"
            onClick={() => setEditModalOpen(true)}
          >
            Editar empresa
          </Button>
        )}
      </Box>

      {/* Company Info Card */}
      <Paper sx={{ p: { xs: 2.5, sm: 3.5 }, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: '20px' }}>
          <InfoField label="Identificador">
            <Typography variant="body1">{company.identifier}</Typography>
          </InfoField>
          <InfoField label="Codigo Corto">
            <Typography variant="body1">{company.short}</Typography>
          </InfoField>
          <InfoField label="Email">
            <Typography variant="body1">{company.email || '\u2014'}</Typography>
          </InfoField>
          <InfoField label="Sitio Web">
            <Typography variant="body1">{company.website || '\u2014'}</Typography>
          </InfoField>
          <InfoField label="Maximo de Usuarios">
            <Typography variant="body1">{company.maxUsers}</Typography>
          </InfoField>
        </Box>
      </Paper>

      {/* Portal de Correspondencia */}
      <Paper sx={{ p: { xs: 2.5, sm: 3.5 }, mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 0.5 }}>
          Portal de Correspondencia
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enlace publico para que usuarios externos radiquen correspondencia en esta empresa.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              minWidth: 0,
              bgcolor: '#e9e9ea',
              px: 2,
              py: 1,
              fontFamily: 'monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {`${window.location.origin}/correspondence/${company.id}/company`}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ContentCopy />}
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/correspondence/${company.id}/company`);
              setCopySnackbar(true);
            }}
          >
            Copiar
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<OpenInNew />}
            href={`/correspondence/${company.id}/company`}
            target="_blank"
          >
            Abrir portal
          </Button>
        </Box>
      </Paper>

      {/* Tabs */}
      <Box sx={{ mb: 3, display: 'inline-flex' }}>
        <Tabs value={currentTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Areas" />
          <Tab label="Tipos de Correspondencia" />
          <Tab label="Bodegas" />
          <Tab label="Cajas" />
          {isOwner && <Tab label="Modulos" />}
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box>
        {currentTab === 0 && <CompanyAreas companyId={company.id} />}
        {currentTab === 1 && <CompanyCorrespondenceTypes companyId={company.id} />}
        {currentTab === 2 && <CompanyWarehouses companyId={company.id} />}
        {currentTab === 3 && <CompanyBoxes companyId={company.id} />}
        {currentTab === 4 && isOwner && (
          <Paper sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>Modulos habilitados</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Activa o desactiva modulos para esta empresa. Los modulos desactivados desaparecen del menu y sus rutas devuelven 403.
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {MODULE_LIST.map(({ key, label, icon: Icon }) => {
                const enabled = modulesConfig[key] !== false;
                return (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Box
                      onClick={() => setModulesConfig(prev => ({ ...prev, [key]: !enabled }))}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 2,
                        border: '1.5px solid',
                        borderColor: enabled ? '#5980a6' : 'rgba(29,31,32,0.16)',
                        bgcolor: enabled ? 'rgba(89,128,166,0.06)' : '#f2f2f3',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: enabled ? '#5980a6' : '#d4d4d7',
                          bgcolor: enabled ? 'rgba(89,128,166,0.09)' : '#e9e9ea',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: enabled ? 'rgba(89,128,166,0.1)' : '#e9e9ea',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 18, color: enabled ? '#5980a6' : '#98989b' }} />
                      </Box>
                      <Typography sx={{ flex: 1, fontSize: '0.875rem', fontWeight: 500, color: enabled ? '#1d1f20' : '#98989b' }}>
                        {label}
                      </Typography>
                      <Switch
                        checked={enabled}
                        size="small"
                        color="primary"
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setModulesConfig(prev => ({ ...prev, [key]: e.target.checked }))}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
            <Button
              variant="contained"
              disabled={modulesSaving}
              onClick={async () => {
                setModulesSaving(true);
                try {
                  await companyService.update(company.id, { modules: modulesConfig });
                  setModulesSaved(true);
                } finally {
                  setModulesSaving(false);
                }
              }}
            >
              Guardar modulos
            </Button>
          </Paper>
        )}
      </Box>

      <Snackbar
        open={copySnackbar}
        autoHideDuration={3000}
        onClose={() => setCopySnackbar(false)}
        message="Enlace copiado al portapapeles"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <Snackbar
        open={modulesSaved}
        autoHideDuration={3000}
        onClose={() => setModulesSaved(false)}
        message="Modulos guardados correctamente"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {isOwner && (
        <CompanyModalForm
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSave={async (data) => {
            await companyService.update(company.id, data);
            setEditModalOpen(false);
            loadCompany();
          }}
          company={company}
        />
      )}
    </Box>
  );
};

export default CompanyDetail;
